import Head from 'next/head'
import Script from 'next/script'
import { motion } from 'framer-motion'
import { FaEnvelope, FaWhatsapp, FaComments, FaMapMarkerAlt, FaPhone, FaHeadset, FaQuestionCircle, FaCreditCard } from 'react-icons/fa'
import { BsStars } from 'react-icons/bs'

export default function Contact() {
  const contactMethods = [
    {
      icon: FaEnvelope,
      title: 'Email Support',
      description: 'For general inquiries or support',
      action: 'support@stringssg.com',
      href: 'mailto:support@stringssg.com',
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-100',
      iconColor: 'text-orange-600'
    },
    {
      icon: FaWhatsapp,
      title: 'WhatsApp Support',
      description: 'For clients with ongoing services',
      action: 'Available in your account',
      href: null,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    {
      icon: FaComments,
      title: 'Platform Chat',
      description: 'Contact your assigned service team',
      action: 'Available in your request',
      href: null,
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600'
    }
  ]

  return (
    <>
      <Head>
        <title>Contact Us - StringsSG</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content="Contact us at support@stringssg.com"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:url" content="https://www.stringssg.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact Us - StringsSG"/>
        <meta property="og:description" content="Contact us at support@stringssg.com" />
        <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/Google-icon.png" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/contact"/>
        <meta name="twitter:title" content="Contact Us - StringsSG"/> 
        <meta name="twitter:description" content="Contact us at support@stringssg.com"/>
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative bg-linear-to-br from-slate-900 via-slate-800 to-orange-900 py-20 md:py-28 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-400 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-400 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>
          </div>
          
          <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-400/30 rounded-full px-4 py-2 text-orange-300 text-sm font-medium mb-6">
                <FaHeadset />
                We&apos;re here to help
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
                Contact Us
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                We are here to assist you! Whether you have questions about our services, your account, or billing, feel free to reach out through the appropriate channels below.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Contact Methods */}
        <div className="max-w-4xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">How Can We Help?</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {contactMethods.map((method, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-xl transition-all duration-300 text-center"
                >
                  <div className={`w-16 h-16 ${method.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <method.icon className={`text-2xl ${method.iconColor}`} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{method.title}</h3>
                  <p className="text-slate-500 text-sm mb-3">{method.description}</p>
                  {method.href ? (
                    <a 
                      href={method.href}
                      className={`inline-flex items-center gap-2 bg-linear-to-r ${method.color} text-white font-semibold px-4 py-2 rounded-lg text-sm transition-all hover:opacity-90`}
                    >
                      {method.action}
                    </a>
                  ) : (
                    <span className="text-slate-600 text-sm font-medium">{method.action}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Support Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="bg-linear-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <FaQuestionCircle className="text-amber-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Existing Services</h3>
                </div>
                <ul className="space-y-4 text-slate-600">
                  <li className="flex gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <div>
                      <strong>Service-Specific Assistance:</strong><br/>
                      Clients with ongoing services can contact us through the WhatsApp chat support line provided under the opened request in your account.
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <div>
                      <strong>Platform Chat:</strong><br/>
                      You may also directly contact the assigned service team using the platform&apos;s chat feature.
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-linear-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FaCreditCard className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Account & Billing</h3>
                </div>
                <ul className="space-y-4 text-slate-600">
                  <li className="flex gap-3">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>Use our WhatsApp support line (accessible through your account).</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>Alternatively, email us at <a href="mailto:support@stringssg.com" className="text-orange-600 hover:text-orange-700 font-medium">support@stringssg.com</a>.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Office Location */}
            <div className="bg-linear-to-br from-orange-500 to-amber-500 rounded-2xl p-8 text-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4">Our Office</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <FaMapMarkerAlt className="text-xl mt-1" />
                      <div>
                        <p className="font-semibold">StringsSG Pte Ltd</p>
                        <p className="text-white/80">Paya Lebar Square<br/>60 Paya Lebar Rd<br/>Singapore 409051</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaEnvelope className="text-xl" />
                      <a href="mailto:support@stringssg.com" className="text-white hover:text-white/80 transition-colors">
                        support@stringssg.com
                      </a>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                    <BsStars className="text-4xl mx-auto mb-3 text-amber-200" />
                    <p className="text-lg font-semibold">RedDotAI Support</p>
                    <p className="text-white/80 text-sm">Powered by our AI assistant</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Script 
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {              
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
    </>
  )
}
